import React, { useState } from 'react';
import { Upload, FileText, CheckCircle, XCircle, AlertTriangle, Download, ArrowLeft } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const InventoryBulkUpdate = () => {
  const { t, language } = useLanguage();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        toast.error(language === 'it' ? 'Seleziona un file CSV' : 'Please select a CSV file');
        return;
      }
      setFile(selectedFile);
      setResult(null);
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error(language === 'it' ? 'Seleziona un file CSV' : 'Please select a CSV file');
      return;
    }

    setUploading(true);
    setError(null);
    setResult(null);

    const formData = new FormData();
    formData.append('file', file);

    let data = null;

    try {

      const response = await fetch(`${BACKEND_URL}/api/inventory-update`, {
        method: 'POST',
        body: formData,
      });


      // Parse response body once
      try {
        data = await response.json();
      } catch (jsonError) {
        throw new Error(response.status === 502 || response.status === 504
          ? `Il server ha impiegato troppo tempo (errore ${response.status}). Riprova tra qualche istante.`
          : `Risposta non valida dal server (errore ${response.status})`);
      }

      if (!response.ok) {
        throw new Error(data?.detail || data?.message || 'Upload failed');
      }


      setResult(data);
      const message = language === 'it'
        ? `✅ Aggiornati ${data.updated || 0} prodotti! ${data.not_found > 0 ? `⚠️ ${data.not_found} non trovati.` : ''}`
        : `✅ Updated ${data.updated || 0} products! ${data.not_found > 0 ? `⚠️ ${data.not_found} not found.` : ''}`;
      toast.success(message);

    } catch (error) {
      const errorMsg = error.message || 'Failed to upload file';
      setError(errorMsg);
      toast.error(`Error: ${errorMsg}`);
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const csvContent = `codice,titolo,quantità
3864900,Copertone Metzeler,15
ABC123,Filtro Aria,0
XYZ789,Catena Trasmissione,25`;

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'template_inventario.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="min-h-screen bg-black text-white py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/admin/inventory"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft size={20} />
            {language === 'it' ? 'Torna a Inventario' : 'Back to Inventory'}
          </Link>
          
          <h1 className="text-4xl font-bold mb-2" style={{ color: 'var(--brand-primary)' }}>
            {language === 'it' ? 'Aggiornamento Inventario Massivo' : 'Bulk Inventory Update'}
          </h1>
          <p className="text-gray-400">
            {language === 'it'
              ? 'Carica un CSV con 3 colonne: codice, titolo, quantità'
              : 'Upload a CSV with 3 columns: codice, titolo, quantità'}
          </p>
        </div>

        {/* Download Template */}
        <div className="bg-white/5 border border-white/10 p-6 mb-6">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
                <FileText size={20} style={{ color: 'var(--brand-primary)' }} />
                {language === 'it' ? 'Template CSV' : 'CSV Template'}
              </h3>
              <p className="text-sm text-gray-400 mb-4">
                {language === 'it'
                  ? 'Il file deve contenere 3 colonne: codice (SKU), titolo (opzionale), quantità (numero)'
                  : 'File must contain 3 columns: codice (SKU), titolo (optional), quantità (number)'}
              </p>
              <div className="bg-black/50 p-4 rounded font-mono text-sm mb-4">
                <div className="text-green-400">codice,titolo,quantità</div>
                <div className="text-gray-300">3864900,Copertone Metzeler,15</div>
                <div className="text-gray-300">ABC123,Filtro Aria,0</div>
                <div className="text-gray-300">XYZ789,Catena,25</div>
              </div>
            </div>
            <button
              onClick={downloadTemplate}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 transition-colors"
            >
              <Download size={18} />
              {language === 'it' ? 'Scarica Template' : 'Download Template'}
            </button>
          </div>
        </div>

        {/* Upload Section */}
        <div className="bg-white/5 border border-white/10 p-8 mb-6">
          <div className="border-2 border-dashed border-white/20 rounded-lg p-8 text-center hover:border-orange-500/50 transition-colors">
            <Upload size={48} className="mx-auto mb-4 text-gray-400" />
            <p className="text-lg mb-4">
              {language === 'it' ? 'Carica file CSV inventario' : 'Upload inventory CSV file'}
            </p>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="hidden"
              id="csv-upload"
            />
            <label
              htmlFor="csv-upload"
              className="inline-block px-6 py-3 bg-white/10 hover:bg-white/20 cursor-pointer transition-colors"
            >
              {language === 'it' ? 'Seleziona File' : 'Select File'}
            </label>

            {file && (
              <div className="mt-4 p-4 bg-white/5 inline-block">
                <FileText size={20} className="inline mr-2" style={{ color: 'var(--brand-primary)' }} />
                <span className="font-medium">{file.name}</span>
                <span className="text-sm text-gray-400 ml-3">
                  ({(file.size / 1024).toFixed(2)} KB)
                </span>
              </div>
            )}
          </div>

          {file && (
            <button
              onClick={handleUpload}
              disabled={uploading}
              className="w-full mt-6 py-4 font-semibold transition-all disabled:opacity-50"
              style={{
                backgroundColor: uploading ? '#666' : 'var(--brand-primary)',
                color: '#000',
              }}
            >
              {uploading
                ? (language === 'it' ? 'Caricamento...' : 'Uploading...')
                : (language === 'it' ? 'Aggiorna Inventario' : 'Update Inventory')}
            </button>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/50 p-6 mb-6">
            <div className="flex items-start gap-3">
              <XCircle size={24} className="text-red-500 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-semibold text-red-500 mb-2">
                  {language === 'it' ? 'Errore' : 'Error'}
                </h3>
                <p className="text-red-300">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Results Display */}
        {result && (
          <div className="space-y-6">
            {/* Summary */}
            <div className="bg-white/5 border border-white/10 p-6">
              <h3 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <CheckCircle size={24} style={{ color: 'var(--brand-primary)' }} />
                {language === 'it' ? 'Riepilogo Aggiornamento' : 'Update Summary'}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-green-500/10 border border-green-500/30 p-4 text-center">
                  <div className="text-3xl font-bold text-green-400">{result.updated}</div>
                  <div className="text-sm text-gray-400">
                    {language === 'it' ? 'Aggiornati' : 'Updated'}
                  </div>
                </div>

                {result.not_found > 0 && (
                  <div className="bg-orange-500/10 border border-orange-500/30 p-4 text-center">
                    <div className="text-3xl font-bold text-orange-400">{result.not_found}</div>
                    <div className="text-sm text-gray-400">
                      {language === 'it' ? 'Non Trovati' : 'Not Found'}
                    </div>
                  </div>
                )}

                {result.errors && result.errors.length > 0 && (
                  <div className="bg-red-500/10 border border-red-500/30 p-4 text-center">
                    <div className="text-3xl font-bold text-red-400">{result.errors.length}</div>
                    <div className="text-sm text-gray-400">
                      {language === 'it' ? 'Errori' : 'Errors'}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Updated Products */}
            {result.updated_products && result.updated_products.length > 0 && (
              <div className="bg-white/5 border border-white/10 p-6">
                <h3 className="text-xl font-semibold mb-4">
                  {language === 'it' ? 'Prodotti Aggiornati' : 'Updated Products'}
                </h3>
                <div className="max-h-96 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-white/5 sticky top-0">
                      <tr>
                        <th className="text-left p-3">SKU</th>
                        <th className="text-left p-3">{language === 'it' ? 'Nome' : 'Name'}</th>
                        <th className="text-center p-3">{language === 'it' ? 'Stock Vecchio' : 'Old Stock'}</th>
                        <th className="text-center p-3">{language === 'it' ? 'Stock Nuovo' : 'New Stock'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.updated_products.map((product) => (
                        <tr key={product.sku} className="border-t border-white/10 hover:bg-white/5">
                          <td className="p-3 font-mono text-orange-400">{product.sku}</td>
                          <td className="p-3">{product.name}</td>
                          <td className="p-3 text-center text-gray-400">{product.old_stock}</td>
                          <td className="p-3 text-center text-green-400 font-semibold">{product.new_stock}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Not Found SKUs */}
            {result.not_found_skus && result.not_found_skus.length > 0 && (
              <div className="bg-orange-500/10 border border-orange-500/30 p-6">
                <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <AlertTriangle size={20} className="text-orange-400" />
                  {language === 'it' ? 'SKU Non Trovati' : 'SKUs Not Found'}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {result.not_found_skus.map((sku) => (
                    <span key={sku} className="px-3 py-1 bg-orange-500/20 text-orange-300 font-mono text-sm">
                      {sku}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Errors */}
            {result.errors && result.errors.length > 0 && (
              <div className="bg-red-500/10 border border-red-500/30 p-6">
                <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <XCircle size={20} className="text-red-400" />
                  {language === 'it' ? 'Errori' : 'Errors'}
                </h3>
                <ul className="space-y-2 text-sm text-red-300">
                  {result.errors.map((err, index) => (
                    <li key={`bulkerr-${index}-${err.substring(0, 20)}`} className="font-mono">• {err}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default InventoryBulkUpdate;
